import { apiRequest } from '@/core';
import type { AmenityCategory } from './referenceData';

export type PaginatedResponse<T> = {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

export type PropertyStatus = {
  id: number;
  name: string;
  slug?: string;
};

export type Property = {
  id: number;
  name?: string;
  description?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  price?: number;
  square_meters?: number | null;
  renovation_year?: number | null;
  number_of_rooms?: number | null;
  number_of_bathrooms?: number | null;
  property_type_id?: number;
  area_id?: number;
  user_id?: number;
  area?: { id: number; name: string };
  user?: { id: number; email: string; display_name: string };
  status_id?: number;
  status?: PropertyStatus;
  completed_step?: number | null;
  published_at?: string | null;
  created_at?: string | null;
  lease_duration?: 'season' | 'year' | null;
  accept_bunkbeds?: boolean;
  bills_included?: boolean;
  contact_for_services?: boolean;
  parking_slots?: boolean;
  has_view?: boolean;
  media?: Array<{
    id: number;
    uuid?: string;
    url: string;
    type?: string;
    mime_type?: string;
    responsive_images?: ResponsiveImages;
  }>;
  amenities?: AmenityCategory[];
  rooms?: Array<{
    id: number;
    square_meters: number;
    single_beds: number;
    double_beds: number;
    media?: Array<{
      id: number;
      uuid?: string;
      url: string;
      type?: string;
      mime_type?: string;
      responsive_images?: ResponsiveImages;
    }>;
    amenities?: AmenityCategory[];
  }>;
};

export type ListPropertiesParams = {
  page?: number;
  perPage?: number;
  sort?: string;
  include?: string[];

  propertyTypeId?: number;
  areaId?: number;
  statusId?: number;
  acceptBunkbeds?: boolean;
  billsIncluded?: boolean;
  amenityIds?: number[];

  name?: string;
  address?: string;

  near?: {
    lat: number;
    lng: number;
    distanceInMeters: number;
  };

  bounds?: {
    south: number;
    west: number;
    north: number;
    east: number;
  };
};

export const listProperties = (params: ListPropertiesParams = {}) =>
  apiRequest<PaginatedResponse<Property>>({
    endpoint: 'v1/properties',
    method: 'GET',
    params: {
      page: params.page,
      per_page: params.perPage,
      sort: params.sort,
      include: params.include?.join(','),

      'filter[property_type_id]': params.propertyTypeId,
      'filter[area_id]': params.areaId,
      'filter[status_id]': params.statusId,
      'filter[accept_bunkbeds]': params.acceptBunkbeds,
      'filter[bills_included]': params.billsIncluded,
      'filter[amenities]': params.amenityIds?.length
        ? params.amenityIds.join(',')
        : undefined,

      'filter[name]': params.name || undefined,
      'filter[address]': params.address || undefined,

      'filter[near]': params.near
        ? `${params.near.lat},${params.near.lng},${params.near.distanceInMeters}`
        : undefined,

      'filter[bounds]': params.bounds
        ? `${params.bounds.south},${params.bounds.west},${params.bounds.north},${params.bounds.east}`
        : undefined,
    },
  });

export type PropertiesForGroupParams = {
  page?: number;
  perPage?: number;
  excludePending?: boolean;
};

export const getPropertiesForGroup = (
  companyRequestGroupId: number,
  params: PropertiesForGroupParams = {},
) =>
  apiRequest<PaginatedResponse<Property>>({
    endpoint: `v1/properties/for-group/${companyRequestGroupId}`,
    method: 'GET',
    params: {
      page: params.page,
      per_page: params.perPage,
      exclude_pending: params.excludePending,
    },
  });

export const getPropertyWithDetails = (
  id: string,
  options?: { skipErrorToast?: boolean },
) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}`,
    method: 'GET',
    params: {
      include: ['amenities', 'rooms.amenities', 'rooms.media', 'media'],
    },
    skipErrorToast: options?.skipErrorToast,
  });

export const getUserPropertyWithDetails = (
  id: string,
  options?: { skipErrorToast?: boolean },
) =>
  apiRequest<Property>({
    endpoint: `v1/properties/user/${id}`,
    method: 'GET',
    params: {
      include: ['amenities', 'rooms.amenities', 'rooms.media', 'media'],
    },
    skipErrorToast: options?.skipErrorToast,
  });

export const listUserProperties = (params: ListPropertiesParams = {}) =>
  apiRequest<PaginatedResponse<Property>>({
    endpoint: 'v1/properties/user',
    method: 'GET',
    params: {
      page: params.page,
      per_page: params.perPage,
      sort: params.sort,
      include: params.include?.join(','),
      'filter[name]': params.name || undefined,
      'filter[status_id]': params.statusId,
      'filter[property_type_id]': params.propertyTypeId,
    },
  });

export const publishProperty = (id: number) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/publish`,
    method: 'POST',
  });

export const approveProperty = (id: number) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/approve`,
    method: 'POST',
  });

export const submitPropertyForReview = (id: number) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/submit`,
    method: 'POST',
  });

export const rejectPropertyReview = (id: number, content = '') =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/reject`,
    method: 'POST',
    body: { content },
  });

export const requestPropertyReviewChanges = (id: number, content: string) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/needs-changes`,
    method: 'POST',
    body: { content },
  });

export const deleteProperty = (id: number) =>
  apiRequest<{ message: string }>({
    endpoint: `v1/properties/${id}`,
    method: 'DELETE',
  });

export type PropertyComment = {
  id: number;
  content: string;
  is_internal: boolean;
  user?: { id: number; email?: string; display_name?: string };
  commentable_type: string;
  commentable_id: number;
  parent_id: number | null;
  created_at: string;
  updated_at: string;
};

export const listPropertyComments = (
  propertyId: number | string,
  signal?: AbortSignal,
  includeInternal = false,
) =>
  apiRequest<{ data: PropertyComment[] }>({
    endpoint: `v1/property/${propertyId}/comments`,
    method: 'GET',
    params: includeInternal ? { 'filter[is_internal]': true } : undefined,
    signal,
  });

type LocalizedString = { en: string; el: string };

export type PropertyStep1Payload = {
  name: LocalizedString;
  description?: LocalizedString;
  area_id?: number;
  address?: LocalizedString;
  latitude?: number | null;
  longitude?: number | null;
  square_meters?: number | null;
  renovation_year?: number | null;
  property_type_id?: number;
};

export const submitPropertyStep1 = (data: PropertyStep1Payload) =>
  apiRequest<Property>({
    endpoint: 'v1/properties/step-1',
    method: 'POST',
    body: data,
  });

export const updatePropertyStep1 = (id: number, data: PropertyStep1Payload) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/step-1`,
    method: 'PUT',
    body: data,
  });

export const createPropertyDraft = (
  data: Partial<PropertyStep1Payload> & { step: number },
) =>
  apiRequest<Property>({
    endpoint: 'v1/properties/draft',
    method: 'POST',
    body: data,
  });

export const updatePropertyDraft = (
  id: number,
  data: Partial<PropertyStep1Payload> & { step: number },
) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/draft`,
    method: 'PUT',
    body: data,
  });

export type ResponsiveImage = {
  fallback_url?: string;
  webp_url?: string;
  width?: number;
  height?: number;
};

export type ResponsiveImages = Partial<
  Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', ResponsiveImage>
>;

export type PropertyMediaItem = {
  uuid: string;
  url: string;
  type?: string;
  mime_type?: string;
  filename?: string;
  name?: string;
  size?: number;
  human_readable_size?: string;
  collection_name?: string;
  responsive_images?: ResponsiveImages;
  processing_status?: string;
};

type MediaUploadResponse = {
  media?: PropertyMediaItem[];
};

type MediaListResponse =
  | PropertyMediaItem[]
  | {
      data?: PropertyMediaItem[];
      media?: PropertyMediaItem[];
    };

const normalizeUploadedMedia = (
  response: PropertyMediaItem | MediaUploadResponse,
): PropertyMediaItem => {
  if ('uuid' in response && 'url' in response) {
    return response;
  }

  if ('media' in response) {
    const [media] = response.media ?? [];
    if (!media) {
      throw new Error('Upload completed without returning media.');
    }

    return media;
  }

  throw new Error('Upload completed without returning media.');
};

export const getMediaPreviewUrl = (
  media: Pick<PropertyMediaItem, 'url' | 'responsive_images'>,
) =>
  media.responsive_images?.sm?.webp_url ||
  media.responsive_images?.sm?.fallback_url ||
  media.responsive_images?.md?.webp_url ||
  media.responsive_images?.md?.fallback_url ||
  media.url;

const normalizeMediaList = (response: MediaListResponse) => {
  if (Array.isArray(response)) {
    return response;
  }

  return response.data ?? response.media ?? [];
};

export const listPropertyMedia = async (propertyId: number) => {
  const response = await apiRequest<MediaListResponse>({
    endpoint: `v1/property/${propertyId}/media`,
    method: 'GET',
    params: {
      collection_name: 'property_gallery',
      per_page: 15,
    },
  });

  return normalizeMediaList(response);
};

export const uploadPropertyMedia = async (propertyId: number, file: File) => {
  const formData = new FormData();
  formData.append('uploads[]', file);
  formData.append('collection_name', 'property_gallery');
  const response = await apiRequest<PropertyMediaItem | MediaUploadResponse>({
    endpoint: `v1/property/${propertyId}/media`,
    method: 'POST',
    body: formData,
  });

  return normalizeUploadedMedia(response);
};

export const deletePropertyMedia = (uuid: string) =>
  apiRequest<void>({
    endpoint: `v1/media/${uuid}`,
    method: 'DELETE',
  });

export const updatePropertyAmenities = (
  propertyId: number,
  amenityIds: number[],
) =>
  apiRequest<void>({
    endpoint: `v1/property/${propertyId}/amenities`,
    method: 'PUT',
    body: { amenities: amenityIds.map((id) => ({ amenity_id: id })) },
  });

export type RoomPayload = {
  square_meters: number;
  single_beds: number;
  double_beds: number;
  amenities?: Array<{ amenity_id: number }>;
};

export type Room = {
  id: number;
  square_meters: number;
  single_beds: number;
  double_beds: number;
  media?: Array<{ uuid: string; url: string; type?: string }>;
  amenities?: Array<{ id: number; name: string }>;
};

export const createRoom = (propertyId: number) =>
  apiRequest<Room>({
    endpoint: `v1/properties/${propertyId}/rooms`,
    method: 'POST',
  });

export const updateRoom = (roomId: number, data: RoomPayload) =>
  apiRequest<Room>({
    endpoint: `v1/rooms/${roomId}`,
    method: 'PUT',
    body: data,
  });

export const deleteRoom = (roomId: number) =>
  apiRequest<void>({
    endpoint: `v1/rooms/${roomId}`,
    method: 'DELETE',
  });

export const uploadRoomMedia = async (roomId: number, file: File) => {
  const formData = new FormData();
  formData.append('uploads[]', file);
  formData.append('collection_name', 'property_gallery');
  const response = await apiRequest<PropertyMediaItem | MediaUploadResponse>({
    endpoint: `v1/room/${roomId}/media`,
    method: 'POST',
    body: formData,
  });

  return normalizeUploadedMedia(response);
};

export const submitPropertyStep2 = (id: number) =>
  apiRequest<void>({
    endpoint: `v1/properties/${id}/step-2`,
    method: 'POST',
  });

export const submitPropertyStep3 = (id: number) =>
  apiRequest<void>({
    endpoint: `v1/properties/${id}/step-3`,
    method: 'POST',
  });

export type Step4Payload = {
  number_of_bathrooms?: number;
  accept_bunkbeds?: boolean;
  parking_slots?: boolean;
  has_view?: boolean;
};

export const submitPropertyStep4 = (id: number, data: Step4Payload) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/step-4`,
    method: 'POST',
    body: data,
  });

export type Step5Payload = {
  price?: number;
  lease_duration?: 'season' | 'year';
  bills_included?: boolean;
  contact_for_services?: boolean;
};

export const submitPropertyStep5 = (id: number, data: Step5Payload) =>
  apiRequest<Property>({
    endpoint: `v1/properties/${id}/step-5`,
    method: 'POST',
    body: data,
  });

export const listFavorites = (
  params: { page?: number; perPage?: number; include?: string[] } = {},
) =>
  apiRequest<PaginatedResponse<Property>>({
    endpoint: 'v1/properties/favorites',
    method: 'GET',
    params: {
      page: params.page,
      per_page: params.perPage,
      include: params.include?.join(','),
    },
  });

export const addFavorite = (propertyId: number) =>
  apiRequest<{ message: string }>({
    endpoint: `v1/properties/${propertyId}/favorite`,
    method: 'POST',
  });

export const removeFavorite = (propertyId: number) =>
  apiRequest<{ message: string }>({
    endpoint: `v1/properties/${propertyId}/favorite`,
    method: 'DELETE',
  });
